import { defineStore } from 'pinia'
import { ref } from 'vue'
import { api } from '@/api/client'
import type { NavItem } from '@/api/types'
import { defaultNav } from '@/content/defaultNav'

// Holds the public subnav items. Starts as the coded default (so the bar renders instantly and even
// if the API is unreachable), then load() replaces it with the saved menu when one exists. An empty or
// failed response keeps the default — matching the ContentText "default until first save" model.
// refresh() re-fetches after an admin save so the live bar updates without a reload.
export const useNavMenuStore = defineStore('navMenu', () => {
  const items = ref<NavItem[]>(defaultNav)
  const loaded = ref(false)

  async function fetchMain(): Promise<void> {
    try {
      const result = await api.get<NavItem[]>('/api/v1/nav/main')
      // Only override the coded default when a non-empty saved menu comes back.
      if (Array.isArray(result) && result.length > 0) {
        items.value = result
      } else {
        items.value = defaultNav
      }
    } catch {
      items.value = defaultNav
    } finally {
      loaded.value = true
    }
  }

  // Load once per app session (idempotent); navigation reuses the in-store value.
  function load(): void {
    if (loaded.value) return
    void fetchMain()
  }

  // Force a re-fetch (used after an admin save).
  function refresh(): Promise<void> {
    return fetchMain()
  }

  return { items, loaded, load, refresh }
})
